import { MLSListing, isDefined } from '@aloft/toolkit-shared';

import { CompSelectionProperty } from '../types.ts';
import { normalizeRentalListingStatus } from './normalizeRentalListingStatus.ts';
import { rentalListingsToCompSelectionProps } from './rentalListingsToCompSelectionProps.ts';

export const MAX_COMPS = 99;

export type RentalRecommendationLists = {
  recommendations: CompSelectionProperty[];
  filteredRecs: CompSelectionProperty[];
};

const passesRentalStatusFilter = (rec: CompSelectionProperty): boolean =>
  isDefined(normalizeRentalListingStatus(rec.mlsStatus));

export const buildRentalRecommendationLists = (
  subjectComp: CompSelectionProperty,
  comps: CompSelectionProperty[],
  listings: MLSListing[],
  hasArvOption: boolean,
): RentalRecommendationLists => {
  const recommendations = rentalListingsToCompSelectionProps(
    subjectComp,
    comps,
    listings,
    hasArvOption,
  )
    .filter(passesRentalStatusFilter)
    .sort(
      (a, b) =>
        (b.similarityScore ?? Number.MIN_VALUE) - (a.similarityScore ?? Number.MIN_VALUE),
    );

  const filteredRecs = recommendations.filter(
    (comp) =>
      comp.address?.AddressLineText !== subjectComp.address?.AddressLineText &&
      comp.listingID !== subjectComp.listingID,
  );

  return { recommendations, filteredRecs };
};

export const selectTopRentalRecommendationsFromListings = (
  listings: MLSListing[],
  subjectComp: CompSelectionProperty,
  comps: CompSelectionProperty[],
  hasArvOption: boolean,
  limit: number,
): MLSListing[] => {
  const { filteredRecs } = buildRentalRecommendationLists(
    subjectComp,
    comps,
    listings,
    hasArvOption,
  );
  const byId = new Map<string, MLSListing>();
  listings.forEach((listing) => {
    const id = listing.ToolkitListingID ?? listing.ListingId;
    if (id && !byId.has(id)) {
      byId.set(id, listing);
    }
  });
  return filteredRecs
    .slice(0, limit)
    .map((c) => byId.get(c.id))
    .filter(isDefined);
};
